import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Layout } from "./components/Layout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import PortalLogin from "./pages/PortalLogin";
import Support from "./pages/Support";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import Claim from "./pages/Claim";
import Help from "./pages/Help";
import Dashboard from "./pages/author/Dashboard";
import NewProject from "./pages/author/NewProject";
import ProjectDetail from "./pages/author/ProjectDetail";
import Sales from "./pages/author/Sales";
import MyStore from "./pages/author/MyStore";
import Marketing from "./pages/author/Marketing";
import MarketingPublic from "./pages/MarketingPublic";
import Profile from "./pages/author/Profile";
import Pipeline from "./pages/admin/Pipeline";
import SalesImport from "./pages/admin/SalesImport";
import Payouts from "./pages/admin/Payouts";
import AuthorDocs from "./pages/admin/AuthorDocs";
import Users from "./pages/admin/Users";
import Reports from "./pages/admin/Reports";
import Settings from "./pages/admin/Settings";
import StoreAdmin from "./pages/admin/StoreAdmin";
import Website from "./pages/admin/Website";
import Store from "./pages/Store";
import About from "./pages/About";
import Contact from "./pages/Contact";
import PublicFAQ from "./pages/PublicFAQ";
import PricingPage from "./pages/PricingPage";
import HelpdeskLanding from "./pages/HelpdeskLanding";
import HelpdeskSignup from "./pages/HelpdeskSignup";
import HelpdeskAdmin from "./pages/admin/HelpdeskAdmin";
import { DeskLayout } from "./components/DeskLayout";
import DeskDashboard from "./pages/desk/DeskDashboard";
import Tickets from "./pages/desk/Tickets";
import TicketDetail from "./pages/desk/TicketDetail";
import KnowledgeBase from "./pages/desk/KnowledgeBase";
import DeskUsers from "./pages/desk/DeskUsers";
import DeskSettings from "./pages/desk/DeskSettings";
import ShareScreen from "./pages/desk/ShareScreen";
import Siem from "./pages/desk/Siem";
import Assets from "./pages/desk/Assets";
import Mdm from "./pages/desk/Mdm";
import DeskAnalytics from "./pages/desk/DeskAnalytics";
import Crm from "./pages/desk/Crm";
import CrmContact from "./pages/desk/CrmContact";

const HD_ROLES = ["hd_admin", "hd_agent", "hd_user"];

const DeskProtected = ({ children, roles = HD_ROLES }) => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  const effRole = user.role === "super_admin" ? "hd_admin" : user.role;
  if (!roles.includes(effRole)) return <Navigate to={user.role === "author" ? "/dashboard" : HD_ROLES.includes(user.role) ? "/desk" : "/admin"} replace />;
  return <DeskLayout>{children}</DeskLayout>;
};

const Protected = ({ children, roles }) => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (user === false) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={user.role === "author" ? "/dashboard" : HD_ROLES.includes(user.role) ? "/desk" : "/admin"} replace />;
  return <Layout>{children}</Layout>;
};

const MarketingRoute = () => {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (user && user.role === "author") return <Layout><Marketing /></Layout>;
  return <MarketingPublic />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/portal/:slug" element={<PortalLogin />} />
          <Route path="/support" element={<Support />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/claim" element={<Claim />} />
          <Route path="/store" element={<Store />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<PublicFAQ />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/help" element={<Protected><Help /></Protected>} />
          <Route path="/dashboard" element={<Protected roles={["author"]}><Dashboard /></Protected>} />
          <Route path="/projects/new" element={<Protected roles={["author"]}><NewProject /></Protected>} />
          <Route path="/projects/:id" element={<Protected><ProjectDetail /></Protected>} />
          <Route path="/sales" element={<Protected roles={["author"]}><Sales /></Protected>} />
          <Route path="/my-store" element={<Protected roles={["author"]}><MyStore /></Protected>} />
          <Route path="/marketing" element={<MarketingRoute />} />
          <Route path="/helpdesk" element={<HelpdeskLanding />} />
          <Route path="/helpdesk/signup" element={<HelpdeskSignup />} />
          <Route path="/desk" element={<DeskProtected><DeskDashboard /></DeskProtected>} />
          <Route path="/desk/tickets" element={<DeskProtected><Tickets /></DeskProtected>} />
          <Route path="/desk/tickets/:id" element={<DeskProtected><TicketDetail /></DeskProtected>} />
          <Route path="/desk/kb" element={<DeskProtected><KnowledgeBase /></DeskProtected>} />
          <Route path="/desk/siem" element={<DeskProtected roles={["hd_admin", "hd_agent"]}><Siem /></DeskProtected>} />
          <Route path="/desk/assets" element={<DeskProtected><Assets /></DeskProtected>} />
          <Route path="/desk/mdm" element={<DeskProtected roles={["hd_admin", "hd_agent"]}><Mdm /></DeskProtected>} />
          <Route path="/desk/analytics" element={<DeskProtected roles={["hd_admin"]}><DeskAnalytics /></DeskProtected>} />
          <Route path="/desk/crm" element={<DeskProtected roles={["hd_admin", "hd_agent"]}><Crm /></DeskProtected>} />
          <Route path="/desk/crm/:id" element={<DeskProtected roles={["hd_admin", "hd_agent"]}><CrmContact /></DeskProtected>} />
          <Route path="/desk/users" element={<DeskProtected roles={["hd_admin"]}><DeskUsers /></DeskProtected>} />
          <Route path="/desk/settings" element={<DeskProtected roles={["hd_admin"]}><DeskSettings /></DeskProtected>} />
          <Route path="/desk/share/:sessionId" element={<DeskProtected><ShareScreen /></DeskProtected>} />
          <Route path="/admin/helpdesk" element={<Protected roles={["super_admin"]}><HelpdeskAdmin /></Protected>} />
          <Route path="/profile" element={<Protected roles={["author"]}><Profile /></Protected>} />
          <Route path="/admin" element={<Protected roles={["admin", "super_admin"]}><Pipeline /></Protected>} />
          <Route path="/admin/imports" element={<Protected roles={["admin", "super_admin"]}><SalesImport /></Protected>} />
          <Route path="/admin/payouts" element={<Protected roles={["admin", "super_admin"]}><Payouts /></Protected>} />
          <Route path="/admin/users" element={<Protected roles={["admin", "super_admin"]}><Users /></Protected>} />
          <Route path="/admin/users/:id/documents" element={<Protected roles={["admin", "super_admin"]}><AuthorDocs /></Protected>} />
          <Route path="/admin/reports" element={<Protected roles={["admin", "super_admin"]}><Reports /></Protected>} />
          <Route path="/admin/store" element={<Protected roles={["admin", "super_admin"]}><StoreAdmin /></Protected>} />
          <Route path="/admin/website" element={<Protected roles={["admin", "super_admin"]}><Website /></Protected>} />
          <Route path="/admin/settings" element={<Protected roles={["super_admin"]}><Settings /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
